import { useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Checklist,
  DeskModal,
  DestinationGroup,
  Fact,
  Facts,
  NotProvided,
  PersonMark,
  RelatedGroup,
  RelatedItem,
  SearchSelect,
  SideDrawer,
  StepTrail,
  SummaryCell,
  SummaryStrip,
  TransitionPanel,
  type ChecklistItem,
  type TrailStep,
} from '@evogard/ui';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@evogard/ui/primitives';
import type {
  Peripheral,
  PeripheralForm,
  PeripheralItem,
  PeripheralQuote,
} from '@evogard/contract';
import {
  PERIPHERAL_ASSIGNEE_LABEL,
  PERIPHERAL_ITEM_PHOTOS,
  type PeripheralItemPhoto,
  PERIPHERAL_PART_ORIGINS,
  PERIPHERAL_QUOTE_STATUSES,
  PERIPHERAL_WORK_STAGES,
  analysisHoldLabel,
  format,
  fromColumn,
  isCatalogFieldRequired,
  isDerivedPeripheralField,
  isPeripheralCorrectablePhase,
  isPeripheralOutcome,
  missingPeripheralFields,
  peripheralItemQuestions,
  peripheralItemTypeLabel,
  peripheralMovesOf,
  peripheralPhaseLabel,
  peripheralPhaseOf,
  peripheralSituationLabel,
  shownPeripheralFields,
  visiblePeripheralFields,
  type CatalogField,
  type CatalogFormState,
  type PeripheralItemType,
  type PeripheralStage,
} from '@evogard/domain';
import { PhaseForm, fieldAnchor, type PhaseFormDesk } from '../claim/PhaseForm.js';
import { ActivityTimeline, FilesList, type DeskVocabulary } from '../claim/DeskTabs.js';
import { ContactDialog, HoldDialog, RecordButtons } from '../claim/DeskRecords.js';
import { LabelsHeader } from '../claim/ClaimLabels.js';
import { DueDateControl, PhaseSlaLine } from '../claim/ClaimDeadlines.js';
import { PeripheralDetails } from './PeripheralDetails.js';
import { ExpensesTab } from '../expenses/ExpensesTab.js';
import { LaunchExpenseDrawer } from '../expenses/LaunchExpenseDrawer.js';
import { useCardExpenses, useExpenseGrants } from '../expenses/useClaimExpenses.js';
import { ItemFormModal, QuoteFormModal } from './PeripheralForms.js';
import { PeripheralQuoteBoard, quoteBoardAnchor } from './PeripheralQuoteBoard.js';
import { ClosureBadge } from '../closures/closureLabels.js';
import { ClosedNotice } from '../closures/ClosedNotice.js';
import {
  PeripheralRefusal,
  peripheralFileUrl,
  usePeripheral,
  usePeripheralActivity,
  usePeripheralAssigneeOptions,
  usePeripheralCommentMutations,
  usePeripheralFiles,
  usePeripheralLabelOptions,
  usePeripheralForm,
  usePeripheralMutations,
  usePeripheralPermissions,
  usePeripheralVisited,
  useSetPeripheralDueDate,
  useSetPeripheralLabels,
} from './usePeripherals.js';

/**
 * O catálogo de Periféricos no formato que o `PhaseForm` lê — o mesmo
 * formulário da Análise e do Reparo, desenhado pelo catálogo do departamento.
 * O acionamento não tem tipo de sinistro, e nenhum campo depende dele.
 */
const PERIPHERAL_FORM_DESK: PhaseFormDesk = {
  shownFields: (phase, state) =>
    shownPeripheralFields(phase as PeripheralStage, state) as CatalogField[],
  visibleFields: (phase) =>
    visiblePeripheralFields(phase as PeripheralStage) as CatalogField[],
  phaseOf: (code) => peripheralPhaseOf(code),
  isDerived: (field) => isDerivedPeripheralField(field as never),
};

/**
 * O mesmo formulário nas etapas do quadro de cotações, sem o contador de itens
 * ("Adicionar Periféricos"): o quadro já mostra os itens, e o mínimo de um
 * continua cobrado na lista da direita, que trava o avanço (15/10/2026).
 */
const withoutItems = (fields: CatalogField[]) =>
  fields.filter((f) => f.binding.subject !== 'items');
const BOARD_FORM_DESK: PhaseFormDesk = {
  ...PERIPHERAL_FORM_DESK,
  shownFields: (phase, state, context) =>
    withoutItems(PERIPHERAL_FORM_DESK.shownFields(phase, state, context)),
  visibleFields: (phase) => withoutItems(PERIPHERAL_FORM_DESK.visibleFields(phase)),
};

/** O nome de um campo de anexo da etapa, ou o da foto do item. */
function peripheralFileFieldLabel(phase: string, kind: string): string {
  if (kind in PERIPHERAL_ITEM_PHOTOS) {
    return `Item · ${PERIPHERAL_ITEM_PHOTOS[kind as PeripheralItemPhoto]}`;
  }
  const field = PERIPHERAL_FORM_DESK.phaseOf(phase)?.fields.find((f) => {
    const b = f.binding as { subject: string; kind?: string };
    return b.subject === 'document' && b.kind === kind;
  });
  return field?.label ?? kind;
}

const phaseNameOf = (phase: string | null) =>
  phase && peripheralPhaseOf(phase) ? peripheralPhaseLabel(phase as PeripheralStage) : '';

/**
 * Como a Atividade e os Anexos da mesa nomeiam as coisas do acionamento
 * (15/10/2026). As frases dos eventos que só o Periférico grava — os itens,
 * as cotações e a PI — moram aqui; as outras são as do sinistro.
 */
const PERIPHERAL_VOCABULARY: DeskVocabulary = {
  noun: 'acionamento',
  commentHint: 'Escreva um comentário sobre o acionamento',
  filesHint:
    'Os arquivos entram pelos campos de anexo de cada etapa e pelas fotos dos itens.',
  departmentName: () => 'Periféricos',
  phaseName: (phase) => phaseNameOf(phase),
  formField: (_department, phase, key) =>
    PERIPHERAL_FORM_DESK.phaseOf(phase)
      ? PERIPHERAL_FORM_DESK.visibleFields(phase).find((f) => f.key === key)
      : undefined,
  fileFieldLabel: (_department, phase, kind) => peripheralFileFieldLabel(phase, kind),
  assigneeLabel: () => PERIPHERAL_ASSIGNEE_LABEL.toLowerCase(),
  sentence: (item) => {
    switch (item.type) {
      case 'aberto':
        return 'abriu o acionamento';
      case 'item_acionado':
        return 'acionou um item';
      case 'item_corrigido':
        return 'corrigiu um item';
      case 'item_retirado':
        return 'tirou um item';
      case 'cotacao_cadastrada':
        return 'cadastrou uma cotação';
      case 'cotacao_corrigida':
        return 'corrigiu uma cotação';
      case 'cotacao_aprovada':
        return 'aprovou uma cotação';
      case 'cotacao_reprovada':
        return 'reprovou uma cotação';
      case 'claims.periferico_aprovado':
        return 'aprovou a solicitação';
      case 'pi_aprovada':
        return 'congelou a conta da PI aprovada';
      case 'whatsapp_pi':
        return item.detail
          ? `não conseguiu enviar a PI por WhatsApp: ${item.detail}`
          : 'enviou a PI por WhatsApp';
      case 'pi_link_novo':
        return 'gerou um link novo de pagamento da PI';
      case 'claims.periferico_pi_reenviada':
        return 'pediu o reenvio da PI por WhatsApp';
      case 'pi_paga':
        return 'recebeu o pagamento da PI pelo link';
      case 'pi_link_expirado':
        return 'viu o link de pagamento da PI expirar sem pagamento';
      /* O `formulario_salvo` gravado antes de 15/10/2026 não tem o antes e o depois. */
      case 'formulario_salvo':
        return item.changes.length === 0
          ? `preencheu campos em ${phaseNameOf(item.phase)}`
          : undefined;
      default:
        return undefined;
    }
  },
};

type MiddleTab = 'formulario' | 'atividade' | 'anexos' | 'detalhes' | 'despesas';

const money = (value: string | null) => {
  const m = fromColumn(value);
  return m === null ? null : format(m);
};

/**
 * A mesa de um acionamento de Periféricos (08/10/2026) — a moldura da mesa do
 * sinistro (`DeskModal`), com o que muda em Periféricos (itens 25 a 27 do
 * README do módulo):
 *
 * - **a coluna da esquerda é a lista de itens acionados**, cada um com a
 *   cotação aprovada (ou a de menor valor, enquanto nenhuma foi aprovada);
 * - **a gaveta do item mostra as cotações da menor para a maior**, com
 *   "Menor valor" e "Preferência de compra". Aprovar e reprovar só existem na
 *   Aprovação do Supervisor, para quem supervisiona;
 * - **a cobertura e a conta da PI ficam no alto**, à vista enquanto se
 *   preenche. A conta é da API: a tela mostra o que ela devolve, e a recusa
 *   dela quando um item não tem cotação aprovada.
 *
 * - **a etapa por onde ele passou se corrige** (15/10/2026, como na Análise):
 *   clicar nela na trilha abre o formulário dela para editar, e o acionamento
 *   continua onde está — o movimento espera a correção acabar;
 * - **o responsável mora no topo** e conta como obrigatório de todo avanço.
 *
 * Desfazer um movimento fica para depois.
 */
/** A contagem ao lado do nome da aba, como na mesa do sinistro. */
const count = (n: number | undefined) =>
  n === undefined ? null : (
    <span className="ml-1.5 rounded-pilula bg-superficie-suave px-1.5 text-xs tabular-nums text-texto-suave">
      {n}
    </span>
  );

export function PeripheralDeskModal({
  peripheralId,
  onClose,
}: {
  peripheralId: number | null;
  onClose: () => void;
}) {
  const query = usePeripheral(peripheralId);
  const p = query.data;
  /**
   * A etapa anterior aberta para corrigir (15/10/2026) — `null` é a atual. O
   * rascunho é sempre da etapa que o miolo mostra.
   */
  const [viewing, setViewing] = useState<string | null>(null);
  const currentForm = usePeripheralForm(peripheralId, p?.stage ?? null);
  const viewingForm = usePeripheralForm(peripheralId, viewing);
  const form = viewing ? viewingForm : currentForm;
  const visited = usePeripheralVisited(peripheralId).data ?? [];
  const perms = usePeripheralPermissions();
  const [draft, setDraft] = useState<Record<string, string | null>>({});
  const [marked, setMarked] = useState<ReadonlySet<string>>(new Set());
  const [dest, setDest] = useState<string | null>(null);
  const [uploading, setUploading] = useState<string | null>(null);
  const [drawerItem, setDrawerItem] = useState<number | null>(null);
  const [itemForm, setItemForm] = useState<{ item: PeripheralItem | null } | null>(null);
  const [quoteForm, setQuoteForm] = useState<{
    itemId: number;
    quote: PeripheralQuote | null;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const mutations = usePeripheralMutations(peripheralId ?? 0);
  /* A mesa abre no Formulário; clicar numa etapa da trilha volta a ele. */
  const [tab, setTab] = useState<MiddleTab>('formulario');
  const activity = usePeripheralActivity(peripheralId);
  const files = usePeripheralFiles(peripheralId);
  /* A aba Despesas (26/10/2026): para quem lança ou supervisiona em Periféricos. */
  const expenseGrants = useExpenseGrants('perifericos');
  const expenseTab = expenseGrants.launch || expenseGrants.supervise;
  const cardExpenses = useCardExpenses('perifericos', peripheralId, expenseTab);
  const [expenseDrawer, setExpenseDrawer] = useState(false);
  const comments = usePeripheralCommentMutations(peripheralId ?? 0);
  const saveLabels = useSetPeripheralLabels(peripheralId ?? 0);
  const labelOptions = usePeripheralLabelOptions();
  const saveDueDate = useSetPeripheralDueDate(peripheralId ?? 0);

  const stage = p?.stage;
  const archived = Boolean(p?.archivedAt);
  const ended = stage ? isPeripheralOutcome(stage) : false;
  const canWork = perms.canMove && !archived && !ended;
  const quoting = stage === 'cotacao' && canWork;
  const approving =
    stage === 'aprovacao_do_supervisor' && !archived && perms.canSupervise;
  const item = p?.items.find((i) => i.id === drawerItem) ?? null;
  /* A etapa que o miolo mostra: a corrigida, ou a atual. */
  const shown = viewing ?? stage;
  /* Na Cotação e na Aprovação, o miolo é o quadro de cotações (15/10/2026). */
  const board =
    p !== undefined &&
    !viewing &&
    (stage === 'cotacao' || stage === 'aprovacao_do_supervisor');
  /* Na Cotação o formulário é só o contador, e o bloco some inteiro. */
  const formDesk = board ? BOARD_FORM_DESK : PERIPHERAL_FORM_DESK;
  const [highlighted, setHighlighted] = useState<number | null>(null);
  const goToItem = (itemId: number) => {
    setHighlighted(itemId);
    document
      .getElementById(quoteBoardAnchor(itemId))
      ?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  };
  const decide = (quoteId: number, decision: 'aprovado' | 'reprovado') => {
    setError(null);
    mutations.decideQuote.mutate(
      { quoteId, decision },
      { onError: (e) => setError(e.message) },
    );
  };

  /* Trocar de acionamento ou de etapa começa um rascunho novo. */
  useEffect(() => {
    setDraft({});
    setMarked(new Set());
    setDest(null);
    setViewing(null);
  }, [peripheralId, stage]);

  useEffect(() => setTab('formulario'), [peripheralId]);

  const view = (phase: string | null) => {
    setDraft({});
    setMarked(new Set());
    setError(null);
    setViewing(phase);
    setTab('formulario');
  };

  /* O que a etapa mostrada tem; o movimento lê sempre o da atual. */
  const state: CatalogFormState = { ...formState(form.data), ...draft };
  const currentState: CatalogFormState = viewing ? formState(currentForm.data) : state;
  /* "Adicionar Periféricos" é a contagem dos itens — o campo a mostra como valor. */
  const formData = form.data && {
    ...form.data,
    values: {
      ...form.data.values,
      ...('items' in form.data.counts && { items: String(form.data.counts.items) }),
    },
  };

  return (
    <>
      <DeskModal
        open={peripheralId !== null}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
        title={
          p ? (
            <span>
              <span className="tabular-nums text-texto-suave">#{p.id}</span> ·{' '}
              {p.vehicle?.memberName ?? 'associado não informado'}
            </span>
          ) : (
            'Carregando…'
          )
        }
        descricao={
          p &&
          [
            'Periféricos',
            p.vehicle
              ? [p.vehicle.plate, p.vehicle.model].filter(Boolean).join(' · ')
              : `Veículo ${p.vehicleCode} (ainda não no cadastro da casa)`,
            p.archivedAt ? 'Arquivado' : null,
          ]
            .filter(Boolean)
            .join(' · ')
        }
        /* Abaixo do nome, como o responsável do sinistro na mesa dele. */
        belowTitle={
          p && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <AssigneePicker peripheral={p} enabled={canWork} />
              {/* O vencimento do card (15/10/2026), como o do sinistro. */}
              <DueDateControl
                of="de Periféricos"
                dueAt={p.dueAt}
                enabled={!archived && !ended}
                save={saveDueDate}
              />
              {/* As etiquetas na mesma linha do responsável e do vencimento (20/10/2026). */}
              <LabelsHeader
                labels={p.labels}
                enabled={!archived && !ended}
                save={saveLabels}
                options={labelOptions}
              />
            </div>
          )
        }
        summary={p && <Summary peripheral={p} />}
        trail={
          p && (
            <>
              <Trail
                peripheral={p}
                dest={dest}
                visited={visited}
                viewing={viewing}
                onView={view}
              />
              {/* O SLA da etapa onde o acionamento está, com o tempo usado (15/10/2026). */}
              <PhaseSlaLine claim={p} phaseName={peripheralPhaseLabel(p.stage)} />
            </>
          )
        }
        leftLabel="Itens acionados"
        left={
          p && (
            <Items
              peripheral={p}
              selected={board ? highlighted : drawerItem}
              onOpen={board ? goToItem : setDrawerItem}
              onAdd={quoting ? () => setItemForm({ item: null }) : undefined}
            />
          )
        }
        middleLabel="Formulário, atividade e anexos"
        right={
          p &&
          stage && (
            <Movement
              peripheral={p}
              hold={currentForm.data?.hold ?? null}
              dest={dest}
              onDest={setDest}
              state={currentState}
              draft={viewing ? {} : draft}
              canWork={canWork}
              correcting={viewing}
              mutations={mutations}
              onSaved={() => setDraft({})}
              onMoved={onClose}
              onMark={(keys) => {
                setMarked(new Set(keys));
                setTab('formulario');
                const first = keys[0];
                if (first) {
                  document
                    .getElementById(fieldAnchor(first))
                    ?.scrollIntoView({ block: 'center' });
                }
              }}
            />
          )
        }
        drawer={
          expenseDrawer && p ? (
            <LaunchExpenseDrawer
              stage="perifericos"
              originId={p.id}
              cardLabel={`Periféricos #${p.id}`}
              onClose={() => setExpenseDrawer(false)}
            />
          ) : (
            item &&
            p && (
              <ItemDrawer
                peripheral={p}
                item={item}
                quoting={quoting}
                approving={approving}
                showQuotes={!board}
                error={error}
                busy={mutations.decideQuote.isPending}
                onClose={() => setDrawerItem(null)}
                onStep={(delta) => {
                  const index = p.items.findIndex((i) => i.id === item.id);
                  const next = p.items[index + delta];
                  if (next) setDrawerItem(next.id);
                }}
                onEditItem={() => setItemForm({ item })}
                onRemoveItem={() =>
                  mutations.archiveItem.mutate(item.id, {
                    onSuccess: () => setDrawerItem(null),
                    onError: (e) => setError(e.message),
                  })
                }
                onNewQuote={() => setQuoteForm({ itemId: item.id, quote: null })}
                onEditQuote={(quote) => setQuoteForm({ itemId: item.id, quote })}
                onDecide={decide}
                onPhoto={(kind, file) =>
                  mutations.attachItemPhoto.mutate(
                    { itemId: item.id, kind, file },
                    { onError: (e) => setError(e.message) },
                  )
                }
              />
            )
          )
        }
        onCloseDrawer={() => {
          setExpenseDrawer(false);
          setDrawerItem(null);
        }}
      >
        {query.isError && <Alert tone="erro">{query.error.message}</Alert>}
        <Tabs value={tab} onValueChange={(v) => setTab(v as MiddleTab)}>
          <TabsList>
            <TabsTrigger value="formulario">Formulário</TabsTrigger>
            <TabsTrigger value="atividade">
              Atividade{count(activity.data?.length)}
            </TabsTrigger>
            <TabsTrigger value="anexos">Anexos{count(files.data?.length)}</TabsTrigger>
            <TabsTrigger value="detalhes">Detalhes</TabsTrigger>
            {expenseTab && (
              <TabsTrigger value="despesas">
                Despesas{count(cardExpenses.data?.expenses.length)}
              </TabsTrigger>
            )}
          </TabsList>
          <TabsContent value="formulario" className="mt-4 flex flex-col gap-3">
            {form.isError && <Alert tone="erro">{form.error.message}</Alert>}
            {viewing && stage && (
              <CorrectionBar
                phase={viewing}
                current={stage}
                readOnly={ended}
                dirty={Object.keys(draft).length > 0}
                saving={mutations.save.isPending}
                error={error}
                onSave={() => {
                  setError(null);
                  mutations.save.mutate(
                    { phase: viewing, values: draft },
                    {
                      onSuccess: () => setDraft({}),
                      onError: (e) => setError(e.message),
                    },
                  );
                }}
                onBack={() => view(null)}
              />
            )}
            {!viewing && currentForm.data?.hold && (
              <Alert
                tone="alerta"
                title={`Parado: ${analysisHoldLabel(currentForm.data.hold.type)}`}
              >
                {currentForm.data.hold.text ?? 'Sem texto registrado.'} Enquanto parado, o
                acionamento não se move: retome-o à direita.
              </Alert>
            )}
            {ended && stage && (
              <Alert tone="informacao">
                O acionamento terminou em {peripheralPhaseLabel(stage)}. Reabrir fica fora
                desta primeira versão.
              </Alert>
            )}
            {board && p && (
              <>
                {error && !drawerItem && <Alert tone="erro">{error}</Alert>}
                <PeripheralQuoteBoard
                  peripheral={p}
                  quoting={quoting}
                  approving={approving}
                  busy={mutations.decideQuote.isPending}
                  highlighted={highlighted}
                  onAddItem={quoting ? () => setItemForm({ item: null }) : undefined}
                  onDetails={setDrawerItem}
                  onNewQuote={(itemId) => setQuoteForm({ itemId, quote: null })}
                  onEditQuote={(itemId, quote) => setQuoteForm({ itemId, quote })}
                  onDecide={decide}
                />
              </>
            )}
            {p &&
              shown &&
              formData &&
              formDesk.shownFields(shown, state, { claimType: 'associado' }).length >
                0 && (
                <PhaseForm
                  desk={formDesk}
                  claimId={p.id}
                  claimType="associado"
                  phase={shown}
                  form={formData}
                  draft={draft}
                  state={state}
                  marked={marked}
                  fileUrl={(documentId) => peripheralFileUrl(p.id, documentId)}
                  uploading={uploading}
                  onChange={(key, value) => {
                    setDraft((d) => ({ ...d, [key]: value }));
                    setMarked((m) => {
                      if (!m.has(key)) return m;
                      const next = new Set(m);
                      next.delete(key);
                      return next;
                    });
                  }}
                  onAttach={(key, files) => {
                    setUploading(key);
                    void Promise.all(
                      files.map((file) =>
                        mutations.attach.mutateAsync({ phase: shown, field: key, file }),
                      ),
                    )
                      .catch((e: Error) => setError(e.message))
                      .finally(() => setUploading(null));
                  }}
                  onArchive={(_key, documentId) =>
                    mutations.archiveDocument.mutate(documentId)
                  }
                  onAddComment={async (key, body) => {
                    await mutations.save.mutateAsync({
                      phase: shown,
                      values: { [key]: body },
                    });
                    setDraft((d) => {
                      const { [key]: _gone, ...rest } = d;
                      return rest;
                    });
                  }}
                />
              )}
          </TabsContent>
          <TabsContent value="atividade" className="mt-4 flex flex-col gap-4">
            {p && stage && canWork && !viewing && (
              <ActivityRecords
                peripheral={p}
                form={currentForm.data}
                mutations={mutations}
              />
            )}
            <ActivityTimeline
              items={activity.data}
              loading={activity.isLoading}
              error={activity.error}
              canComment={perms.canComment && !archived && !ended}
              mutations={comments}
              vocabulary={PERIPHERAL_VOCABULARY}
            />
          </TabsContent>
          <TabsContent value="anexos" className="mt-4">
            <FilesList
              files={files.data}
              loading={files.isLoading}
              error={files.error}
              vocabulary={PERIPHERAL_VOCABULARY}
              resolveUrl={(documentId) => peripheralFileUrl(p?.id ?? 0, documentId)}
            />
          </TabsContent>
          <TabsContent value="detalhes" className="mt-4">
            {p && (
              <PeripheralDetails
                peripheral={p}
                form={currentForm.data}
                files={files.data}
                canWork={canWork}
                regenerate={mutations.regeneratePaymentLink}
              />
            )}
          </TabsContent>
          {expenseTab && p && (
            <TabsContent value="despesas" className="mt-4">
              <ExpensesTab
                stage="perifericos"
                originId={p.id}
                onLaunch={() => {
                  setDrawerItem(null);
                  setExpenseDrawer(true);
                }}
              />
            </TabsContent>
          )}
        </Tabs>
      </DeskModal>

      {p && itemForm && (
        <ItemFormModal
          item={itemForm.item}
          busy={mutations.createItem.isPending || mutations.updateItem.isPending}
          onClose={() => setItemForm(null)}
          onSubmit={(body) => {
            const done = { onSuccess: () => setItemForm(null) };
            if (itemForm.item) {
              mutations.updateItem.mutate({ itemId: itemForm.item.id, body }, done);
            } else {
              mutations.createItem.mutate(body, done);
            }
          }}
          error={
            (mutations.createItem.error ?? mutations.updateItem.error)?.message ?? null
          }
        />
      )}
      {p && quoteForm && (
        <QuoteFormModal
          quote={quoteForm.quote}
          busy={mutations.createQuote.isPending || mutations.updateQuote.isPending}
          onClose={() => setQuoteForm(null)}
          onSubmit={(body) => {
            const done = { onSuccess: () => setQuoteForm(null) };
            if (quoteForm.quote) {
              mutations.updateQuote.mutate({ quoteId: quoteForm.quote.id, body }, done);
            } else {
              mutations.createQuote.mutate({ itemId: quoteForm.itemId, body }, done);
            }
          }}
          error={
            (mutations.createQuote.error ?? mutations.updateQuote.error)?.message ?? null
          }
        />
      )}
    </>
  );
}

/**
 * O responsável do card, no topo da mesa — o mesmo seletor do sinistro
 * (`AssigneePicker` de `ClaimDeskModal`). Até 15/10/2026 ele era o primeiro
 * campo da Entrada; saiu de lá para ter um lugar só. Escolher grava na hora;
 * "Tirar" fica no pé da lista. Quem não pode mover, ou o card arquivado ou
 * encerrado, vê o nome e não abre nada.
 */
function AssigneePicker({
  peripheral: p,
  enabled,
}: {
  peripheral: Peripheral;
  enabled: boolean;
}) {
  const options = usePeripheralAssigneeOptions(enabled);
  const { setAssignee } = usePeripheralMutations(p.id);
  const assignee = p.assignee;
  if (!enabled) {
    return (
      <PersonMark
        name={assignee?.name ?? null}
        emptyLabel="Sem responsável"
        className="max-w-56 text-sm"
      />
    );
  }
  /* Quem já é responsável e deixou de estar ativo continua aparecendo. */
  const list = options.data ?? [];
  const choices =
    assignee && !list.some((x) => x.id === assignee.id)
      ? [{ id: assignee.id, name: assignee.name }, ...list]
      : list;
  return (
    <div className="flex flex-col items-start gap-1">
      <SearchSelect
        label="Responsável"
        options={choices.map((x) => ({ value: x.id, label: x.name }))}
        value={assignee?.id ?? ''}
        onChange={(v) => setAssignee.mutate(v === '' ? null : v)}
        searchPlaceholder="Buscar pessoa"
        emptyMessage="Ninguém com esse nome nesta base."
        loading={options.isLoading}
        clearable
        sortable
        compact={{
          icon: 'pessoa',
          filled: assignee !== null,
          text: assignee?.name,
          title: assignee ? `Responsável: ${assignee.name}` : 'Escolher o responsável',
          loading: setAssignee.isPending,
        }}
      />
      {(options.isError || setAssignee.isError) && (
        <p role="alert" className="max-w-48 text-xs text-erro">
          {(options.error ?? setAssignee.error)?.message}
        </p>
      )}
    </div>
  );
}

/** O alto da mesa: a cobertura, o plano, a situação do carro e a conta da PI. */
function Summary({ peripheral: p }: { peripheral: Peripheral }) {
  const t = p.totals;
  return (
    <SummaryStrip label="Resumo do acionamento">
      <SummaryCell
        value={p.coveragePercent === null ? <NotProvided /> : `${p.coveragePercent}%`}
        detail="Cobertura · do contrato do veículo"
      />
      <SummaryCell value={p.vehicle?.plan ?? <NotProvided />} detail="Plano do veículo" />
      <SummaryCell
        value={p.vehicle?.status ?? <NotProvided />}
        detail="Situação do veículo no SGA"
      />
      <SummaryCell
        value={p.items.length}
        detail={p.items.length === 1 ? 'Item' : 'Itens'}
      />
      {t.ok ? (
        <>
          <SummaryCell
            value={money(t.memberShare)}
            detail={
              t.frozen ? 'PI do associado · congelada' : 'PI do associado · calculada'
            }
          />
          <SummaryCell value={money(t.associationShare)} detail="Parte da associação" />
          <SummaryCell value={money(t.totalApproved)} detail="Total aprovado" />
        </>
      ) : (
        <SummaryCell value="Conta pendente" detail={t.message} />
      )}
    </SummaryStrip>
  );
}

/** As 7 etapas, com a de agora marcada e o destino escolhido em evidência. */
/**
 * As etapas de trabalho na ordem, e onde o acionamento está. **A etapa por
 * onde ele passou é botão** (15/10/2026, como na Análise): abre o formulário
 * dela para corrigir (`isPeripheralCorrectablePhase`, a mesma regra que a API
 * usa para aceitar a gravação). A atual vira botão enquanto outra está
 * aberta — é o caminho de volta.
 */
function Trail({
  peripheral: p,
  dest,
  visited,
  viewing,
  onView,
}: {
  peripheral: Peripheral;
  dest: string | null;
  /** As etapas das passagens já fechadas. */
  visited: readonly string[];
  viewing: string | null;
  onView: (phase: string | null) => void;
}) {
  const current = p.stage as PeripheralStage;
  const currentIndex = (PERIPHERAL_WORK_STAGES as readonly string[]).indexOf(current);
  const open = !p.archivedAt && !isPeripheralOutcome(current);
  /* O encerrado é só consulta (22/10/2026): a etapa por onde passou abre o
     que foi registrado nela, sem corrigir. */
  const consulting = isPeripheralOutcome(current);
  const steps: TrailStep[] = PERIPHERAL_WORK_STAGES.map((code, index) => {
    const now = index === currentIndex;
    const correctable =
      (open && isPeripheralCorrectablePhase(code, current, visited)) ||
      (consulting && code !== viewing && visited.includes(code));
    return {
      key: code,
      label: peripheralPhaseLabel(code),
      state: currentIndex === -1 || index < currentIndex ? 'done' : now ? 'now' : 'next',
      selected: viewing === code || (viewing === null && dest === code),
      onSelect: correctable
        ? () => onView(code)
        : now && viewing !== null
          ? () => onView(null)
          : undefined,
    };
  });
  return (
    <StepTrail
      label="Etapas do acionamento"
      steps={steps}
      aside={
        isPeripheralOutcome(p.stage) ? (
          <ClosureBadge origin="perifericos" phase={p.stage} />
        ) : p.situation ? (
          <Badge tone="progresso">{peripheralSituationLabel(p.situation)}</Badge>
        ) : undefined
      }
    />
  );
}

/** O que o formulário tem preenchido, pelo nome do campo — o que conta o obrigatório. */
function formState(form: PeripheralForm | undefined): CatalogFormState {
  return {
    ...(form?.values ?? {}),
    ...(form?.counts ?? {}),
    ...Object.fromEntries(
      Object.entries(form?.files ?? {}).map(([k, v]) => [k, v.length]),
    ),
  };
}

/**
 * A faixa de quem corrige uma etapa anterior — a mesma do sinistro
 * (`CorrectionBar` de `ClaimDeskModal`): diz que o acionamento não saiu do
 * lugar, grava a correção e leva de volta à etapa atual. Anexo sobe na hora,
 * como na atual; o rascunho é o que este "Salvar" grava.
 */
function CorrectionBar({
  phase,
  current,
  readOnly,
  dirty,
  saving,
  error,
  onSave,
  onBack,
}: {
  phase: string;
  current: string;
  /** O acionamento está encerrado: a etapa de antes abre só para consulta (22/10/2026). */
  readOnly: boolean;
  dirty: boolean;
  saving: boolean;
  error: string | null;
  onSave: () => void;
  onBack: () => void;
}) {
  if (readOnly) {
    return (
      <Alert tone="informacao" title={`Consultando ${peripheralPhaseLabel(phase)}`}>
        <p>
          O acionamento está encerrado em {peripheralPhaseLabel(current)}. Aqui está, só
          para leitura, o que foi registrado nesta etapa.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button size="sm" variant="secundario" onClick={onBack}>
            Voltar para {peripheralPhaseLabel(current)}
          </Button>
        </div>
      </Alert>
    );
  }
  return (
    <Alert tone="informacao" title={`Corrigindo ${peripheralPhaseLabel(phase)}`}>
      <p>
        O acionamento continua em {peripheralPhaseLabel(current)}. O que você salvar aqui
        corrige o que foi registrado nesta etapa, e fica na Atividade.
      </p>
      {error && <p className="mt-1 text-erro">{error}</p>}
      <div className="mt-3 flex flex-wrap gap-2">
        <Button size="sm" disabled={!dirty || saving} loading={saving} onClick={onSave}>
          Salvar correção
        </Button>
        <Button size="sm" variant="secundario" onClick={onBack}>
          Voltar para {peripheralPhaseLabel(current)}
        </Button>
      </div>
    </Alert>
  );
}

/** A cotação que o item mostra na lista: a aprovada, ou a de menor valor. */
function shownQuote(item: PeripheralItem): PeripheralQuote | null {
  return (
    item.quotes.find((q) => q.status === 'aprovado') ??
    item.quotes.find((q) => q.lowest) ??
    null
  );
}

function Items({
  peripheral: p,
  selected,
  onOpen,
  onAdd,
}: {
  peripheral: Peripheral;
  selected: number | null;
  onOpen: (id: number) => void;
  onAdd?: () => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      <RelatedGroup
        title="Itens"
        count={p.items.length}
        empty={
          p.stage === 'entrada'
            ? 'Os itens se acionam na Cotação.'
            : 'Nenhum item acionado ainda.'
        }
      >
        {p.items.map((item) => {
          const quote = shownQuote(item);
          return (
            <RelatedItem
              key={item.id}
              name={peripheralItemTypeLabel(item.itemType)}
              selected={selected === item.id}
              onOpen={() => onOpen(item.id)}
              openLabel={`Ver ${peripheralItemTypeLabel(item.itemType)}`}
              subtitle={
                quote
                  ? `${money(quote.quotedValue) ?? 'sem valor'} · ${
                      PERIPHERAL_QUOTE_STATUSES[
                        quote.status as keyof typeof PERIPHERAL_QUOTE_STATUSES
                      ] ?? quote.status
                    }`
                  : item.quotes.length === 0
                    ? 'Sem cotação'
                    : 'Cotações sem valor'
              }
            />
          );
        })}
      </RelatedGroup>
      {onAdd && (
        <Button variant="secundario" icon="mais" onClick={onAdd}>
          Acionar item
        </Button>
      )}
    </div>
  );
}

/**
 * Para onde o acionamento pode ir, o que falta, e parar ou retomar. Os
 * destinos vêm do catálogo; avançar pede os obrigatórios e voltar não — a API
 * decide igual, e a recusa dela aparece aqui.
 */
function Movement({
  peripheral: p,
  hold,
  dest,
  onDest,
  state,
  draft,
  canWork,
  correcting,
  mutations,
  onSaved,
  onMoved,
  onMark,
}: {
  peripheral: Peripheral;
  hold: { type: string; text: string | null } | null;
  dest: string | null;
  onDest: (dest: string | null) => void;
  state: CatalogFormState;
  draft: Record<string, string | null>;
  canWork: boolean;
  /** A etapa anterior aberta para corrigir — o movimento espera ela acabar. */
  correcting: string | null;
  mutations: ReturnType<typeof usePeripheralMutations>;
  onSaved: () => void;
  /** Moveu: a mesa fecha (decisão do dono em 15/10/2026). */
  onMoved: () => void;
  onMark: (keys: string[]) => void;
}) {
  const [refusal, setRefusal] = useState<string | null>(null);
  const [withdrawing, setWithdrawing] = useState(false);
  const stage = p.stage as PeripheralStage;
  const { forward, outcomes, back } = peripheralMovesOf(stage, hold?.type ?? null);
  const named = (codes: string[]) =>
    codes.map((code) => ({ id: code, name: peripheralPhaseLabel(code) }));
  const confirmsWithdrawal = hold?.type === 'pre_desistencia' && dest === 'desistencia';
  const isForward =
    dest !== null &&
    !confirmsWithdrawal &&
    (forward.includes(dest as never) || outcomes.includes(dest as never));
  const context = { claimType: 'associado' as const };
  const required = shownPeripheralFields(stage, state).filter((f) =>
    isCatalogFieldRequired(f, state, context),
  );
  const missing = missingPeripheralFields(stage, state);
  const missingKeys = new Set(missing.map((f) => f.label));
  /* O card não avança sem dono: o responsável do topo conta como obrigatório. */
  const noAssignee = p.assignee === null;
  const pending = missing.length + (noAssignee ? 1 : 0);
  const items: ChecklistItem[] = [
    {
      key: 'assignee',
      label: `${PERIPHERAL_ASSIGNEE_LABEL} (no topo da mesa)`,
      status: noAssignee ? 'pending' : 'done',
    },
    ...required.map((f) => ({
      key: f.key ?? f.label,
      label: f.label,
      status: missingKeys.has(f.label) ? ('pending' as const) : ('done' as const),
      onSelect: f.key ? () => onMark([f.key!]) : undefined,
    })),
  ];
  const dirty = Object.keys(draft).length > 0;
  const busy = mutations.save.isPending || mutations.move.isPending;

  const go = async () => {
    if (!dest) return;
    setRefusal(null);
    if (isForward && pending > 0) {
      onMark(missing.map((f) => f.key).filter((k): k is string => Boolean(k)));
      if (noAssignee)
        setRefusal('Escolha o responsável no topo da mesa antes de avançar.');
      return;
    }
    try {
      if (dirty) {
        await mutations.save.mutateAsync({ phase: stage, values: draft });
        onSaved();
      }
      await mutations.move.mutateAsync({ fromPhase: stage, toPhase: dest });
      onDest(null);
      onMoved();
    } catch (e) {
      const r = e instanceof PeripheralRefusal ? e : null;
      setRefusal(
        r?.message ?? (e instanceof Error ? e.message : 'Não foi possível mover.'),
      );
    }
  };

  if (isPeripheralOutcome(stage)) {
    return (
      <TransitionPanel action={null}>
        <ClosedNotice
          what="o acionamento"
          target={{
            source: 'periferico',
            id: p.id,
            origin: 'perifericos',
            phase: stage,
            fromPhase: null,
          }}
        />
      </TransitionPanel>
    );
  }

  return (
    <TransitionPanel
      status={
        <>
          {refusal && <Alert tone="erro">{refusal}</Alert>}
          {!canWork && (
            <p className="text-xs text-texto-suave">
              {p.archivedAt
                ? 'Acionamento arquivado não se move.'
                : 'Você vê o acionamento, mas mover e preencher pedem outra permissão.'}
            </p>
          )}
          {correcting && (
            <p className="text-xs text-texto-suave">
              Você está corrigindo {peripheralPhaseLabel(correcting)}. Volte para{' '}
              {peripheralPhaseLabel(stage)} para mover o acionamento.
            </p>
          )}
          {dest && !correcting && (
            <Badge tone={!isForward || pending === 0 ? 'sucesso' : 'alerta'}>
              {!isForward
                ? confirmsWithdrawal
                  ? 'A desistência não exige o formulário da etapa'
                  : 'Voltar não exige o formulário desta fase'
                : pending === 0
                  ? 'Pronto para avançar'
                  : `${pending} ${pending === 1 ? 'obrigatório pendente' : 'obrigatórios pendentes'}`}
            </Badge>
          )}
          {dirty && canWork && !correcting && (
            <Button
              variant="secundario"
              loading={mutations.save.isPending}
              disabled={busy}
              onClick={() =>
                mutations.save.mutate(
                  { phase: stage, values: draft },
                  { onSuccess: onSaved, onError: (e) => setRefusal(e.message) },
                )
              }
            >
              Salvar sem mover
            </Button>
          )}
        </>
      }
      action={
        <Button
          disabled={!dest || !canWork || busy || correcting !== null}
          loading={busy}
          onClick={() => void go()}
        >
          {dest
            ? `${dirty ? 'Salvar e mover' : 'Mover'} para ${peripheralPhaseLabel(dest)}`
            : 'Escolha um destino'}
        </Button>
      }
    >
      <Checklist
        title={`O que ${peripheralPhaseLabel(stage)} pede`}
        items={items}
        empty="Esta etapa não tem obrigatório."
      />
      <DestinationGroup
        title="Avançar para"
        direction="forward"
        destinations={named(forward)}
        selected={dest}
        onSelect={(next) => {
          setRefusal(null);
          onDest(dest === next ? null : next);
        }}
      />
      <DestinationGroup
        title={
          hold?.type === 'pre_desistencia'
            ? 'Confirmar o pedido'
            : 'Encerrar o acionamento'
        }
        direction="forward"
        destinations={named(outcomes)}
        selected={dest}
        onSelect={(next) => {
          setRefusal(null);
          onDest(dest === next ? null : next);
        }}
      />
      <DestinationGroup
        title="Voltar para"
        direction="back"
        destinations={named(back)}
        selected={dest}
        onSelect={(next) => {
          setRefusal(null);
          onDest(dest === next ? null : next);
        }}
      />
      {canWork &&
        (hold ? (
          <Button
            variant="contorno"
            loading={mutations.resume.isPending}
            onClick={() =>
              mutations.resume.mutate(
                { fromPhase: stage },
                { onError: (e) => setRefusal(e.message) },
              )
            }
          >
            Retomar ({analysisHoldLabel(hold.type)})
          </Button>
        ) : (
          /* A tentativa de contato e a pendência moram na aba Atividade
             (19/10/2026), como na Análise; o pedido de desistência fica aqui,
             como destino. */
          <DestinationGroup
            title="Fase geral do acionamento"
            direction="forward"
            destinations={[{ id: 'pre_desistencia', name: 'Pedido de desistência' }]}
            selected={null}
            onSelect={() => setWithdrawing(true)}
          />
        ))}
      {withdrawing && (
        <HoldDialog
          type="pre_desistencia"
          description={`O acionamento fica parado em ${peripheralPhaseLabel(stage)} até o associado confirmar a desistência ou voltar atrás.`}
          submit={(input) =>
            mutations.hold.mutateAsync({
              fromPhase: stage,
              type: 'pre_desistencia',
              text: input.text,
            })
          }
          onClose={() => setWithdrawing(false)}
        />
      )}
    </TransitionPanel>
  );
}

/**
 * A tentativa de contato e a pendência, no alto da aba Atividade (19/10/2026)
 * — as mesmas peças da Análise (`DeskRecords.tsx`), com as rotas do
 * acionamento. Nenhuma das duas tira o acionamento da etapa: o contato fica na
 * Atividade, e a pendência o para até alguém retomar, no movimento.
 */
function ActivityRecords({
  peripheral: p,
  form,
  mutations,
}: {
  peripheral: Peripheral;
  form: PeripheralForm | undefined;
  mutations: ReturnType<typeof usePeripheralMutations>;
}) {
  const [open, setOpen] = useState<'contato' | 'pendencia' | 'juridico' | null>(null);
  const stage = p.stage;
  const phaseName = peripheralPhaseLabel(stage);
  return (
    <>
      <RecordButtons
        held={Boolean(form?.hold)}
        heldTitle="O acionamento já está parado: retome antes."
        onOpen={setOpen}
      />
      {open === 'contato' && (
        <ContactDialog
          effect={`Fica na Atividade, e o acionamento continua em ${phaseName}.`}
          phase={stage}
          suggestions={{
            phones: form?.contactSuggestions.phones ?? [],
            emails: form?.contactSuggestions.emails ?? [],
            workshop: null,
          }}
          submit={(input) => mutations.registerContact.mutateAsync(input)}
          onClose={() => setOpen(null)}
        />
      )}
      {(open === 'pendencia' || open === 'juridico') && (
        <HoldDialog
          type={open}
          description={`O acionamento fica parado em ${phaseName} até alguém retomá-lo.`}
          submit={(input) =>
            mutations.hold.mutateAsync({
              fromPhase: stage,
              type: open,
              text: input.text,
            })
          }
          onClose={() => setOpen(null)}
        />
      )}
    </>
  );
}

/**
 * A gaveta do item: o questionário, as fotos e as cotações da menor para a
 * maior. Aprovar e reprovar só aparecem na Aprovação do Supervisor, para
 * quem supervisiona; nas outras fases a cotação diz o estado e onde se aprova.
 */
function ItemDrawer({
  peripheral: p,
  item,
  quoting,
  approving,
  showQuotes,
  error,
  busy,
  onClose,
  onStep,
  onEditItem,
  onRemoveItem,
  onNewQuote,
  onEditQuote,
  onDecide,
  onPhoto,
}: {
  peripheral: Peripheral;
  item: PeripheralItem;
  quoting: boolean;
  approving: boolean;
  /** Fora da Cotação e da Aprovação; nelas, as cotações moram no quadro do miolo. */
  showQuotes: boolean;
  error: string | null;
  busy: boolean;
  onClose: () => void;
  onStep: (delta: number) => void;
  onEditItem: () => void;
  onRemoveItem: () => void;
  onNewQuote: () => void;
  onEditQuote: (quote: PeripheralQuote) => void;
  onDecide: (quoteId: number, decision: 'aprovado' | 'reprovado') => void;
  onPhoto: (kind: string, file: File) => void;
}) {
  const index = p.items.findIndex((i) => i.id === item.id);
  const sorted = [...item.quotes].sort((a, b) => {
    const va = fromColumn(a.quotedValue);
    const vb = fromColumn(b.quotedValue);
    if (va === null) return 1;
    if (vb === null) return -1;
    return va - vb;
  });
  const questions = peripheralItemQuestions(item.itemType as PeripheralItemType);

  return (
    <SideDrawer
      eyebrow={`Item ${index + 1} de ${p.items.length}`}
      title={peripheralItemTypeLabel(item.itemType)}
      onClose={onClose}
      closeLabel="Fechar o item"
      actions={
        <div className="flex flex-wrap gap-2">
          <Button
            variant="contorno"
            size="sm"
            disabled={index <= 0}
            onClick={() => onStep(-1)}
          >
            Anterior
          </Button>
          <Button
            variant="contorno"
            size="sm"
            disabled={index >= p.items.length - 1}
            onClick={() => onStep(1)}
          >
            Próximo
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-5">
        {error && <Alert tone="erro">{error}</Alert>}

        {showQuotes && (
          <section className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-sm font-semibold">Cotações</h3>
              {quoting && (
                <Button size="sm" icon="mais" onClick={onNewQuote}>
                  Nova cotação
                </Button>
              )}
            </div>
            {sorted.length === 0 && (
              <p className="text-sm text-texto-suave">
                Nenhuma cotação ainda.{' '}
                {quoting ? '' : 'As cotações se cadastram na Cotação.'}
              </p>
            )}
            {sorted.map((q) => (
              <QuoteCard
                key={q.id}
                quote={q}
                quoting={quoting}
                approving={approving}
                busy={busy}
                onEdit={() => onEditQuote(q)}
                onDecide={(decision) => onDecide(q.id, decision)}
              />
            ))}
            {!approving && sorted.some((q) => q.status === 'aguardando_aprovacao') && (
              <p className="text-xs text-texto-suave">
                Aprovar e reprovar é do supervisor, na{' '}
                {peripheralPhaseLabel('aprovacao_do_supervisor')}.
              </p>
            )}
          </section>
        )}

        <section className="flex flex-col gap-3">
          <h3 className="text-sm font-semibold">A peça</h3>
          <Facts>
            <Fact label="Avaria pré-existente">
              {item.preExistingDamage === null ? (
                <NotProvided />
              ) : item.preExistingDamage ? (
                'Sim'
              ) : (
                'Não'
              )}
            </Fact>
            {questions.map((q) => {
              const value = item.answers[q.column];
              return (
                <Fact key={q.column} label={q.label} wide>
                  {value ? (
                    (q.options.find((o) => o.value === value)?.label ?? value)
                  ) : (
                    <NotProvided />
                  )}
                </Fact>
              );
            })}
            {item.notes && (
              <Fact label="Observações" wide>
                {item.notes}
              </Fact>
            )}
          </Facts>
          {quoting && (
            <div className="flex flex-wrap gap-2">
              <Button variant="secundario" size="sm" onClick={onEditItem}>
                Corrigir o item
              </Button>
              <Button variant="contorno" size="sm" onClick={onRemoveItem}>
                Tirar do acionamento
              </Button>
            </div>
          )}
        </section>

        <section className="flex flex-col gap-3">
          <h3 className="text-sm font-semibold">Fotos</h3>
          <ul className="flex flex-col gap-2">
            {Object.entries(PERIPHERAL_ITEM_PHOTOS).map(([kind, label]) => {
              const photos = item.photos.filter((ph) => ph.kind === kind);
              return (
                <li key={kind} className="flex flex-col gap-1 text-sm">
                  <span className="text-texto-suave">{label}</span>
                  {photos.map((ph) => (
                    <button
                      key={ph.id}
                      type="button"
                      className="w-fit text-left underline underline-offset-2"
                      onClick={() =>
                        void peripheralFileUrl(p.id, ph.id).then((url) =>
                          window.open(url, '_blank', 'noopener'),
                        )
                      }
                    >
                      {ph.fileName}
                    </button>
                  ))}
                  {photos.length === 0 &&
                    !p.archivedAt &&
                    !isPeripheralOutcome(p.stage) && (
                      <label className="w-fit cursor-pointer text-xs underline underline-offset-2">
                        Enviar foto
                        <input
                          type="file"
                          accept="image/*"
                          className="sr-only"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) onPhoto(kind, file);
                            e.target.value = '';
                          }}
                        />
                      </label>
                    )}
                </li>
              );
            })}
          </ul>
        </section>
      </div>
    </SideDrawer>
  );
}

function QuoteCard({
  quote: q,
  quoting,
  approving,
  busy,
  onEdit,
  onDecide,
}: {
  quote: PeripheralQuote;
  quoting: boolean;
  approving: boolean;
  busy: boolean;
  onEdit: () => void;
  onDecide: (decision: 'aprovado' | 'reprovado') => void;
}) {
  const statusLabel =
    PERIPHERAL_QUOTE_STATUSES[q.status as keyof typeof PERIPHERAL_QUOTE_STATUSES] ??
    q.status;
  return (
    <div className="flex flex-col gap-2 rounded-borda border border-borda p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-base font-semibold tabular-nums">
          {money(q.quotedValue) ?? 'Sem valor'}
        </span>
        <Badge
          tone={
            q.status === 'aprovado'
              ? 'sucesso'
              : q.status === 'reprovado'
                ? 'erro'
                : 'neutro'
          }
        >
          {statusLabel}
        </Badge>
        {q.lowest && <Badge tone="informacao">Menor valor</Badge>}
        {q.purchasePreference && <Badge tone="progresso">Preferência de compra</Badge>}
      </div>
      <p className="text-sm text-texto-suave">
        {[
          q.providerName ?? q.vendorName,
          q.brand,
          q.partOrigin
            ? (PERIPHERAL_PART_ORIGINS[
                q.partOrigin as keyof typeof PERIPHERAL_PART_ORIGINS
              ] ?? q.partOrigin)
            : null,
        ]
          .filter(Boolean)
          .join(' · ') || 'Fornecedor não informado'}
      </p>
      {(quoting || approving) && (
        <div className="flex flex-wrap gap-2">
          {quoting && q.status !== 'aprovado' && (
            <Button variant="secundario" size="sm" onClick={onEdit}>
              Corrigir
            </Button>
          )}
          {approving && q.status !== 'aprovado' && q.quotedValue !== null && (
            <Button size="sm" loading={busy} onClick={() => onDecide('aprovado')}>
              Aprovar
            </Button>
          )}
          {approving && q.status !== 'reprovado' && (
            <Button
              variant="contorno"
              size="sm"
              disabled={busy}
              onClick={() => onDecide('reprovado')}
            >
              Reprovar
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
